import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import WalterLoader from '../../../../shared/WalterLoader';
import AeratorSubModule from './AeratorSubModule.js';
import ShowerSubModule from './ShowerSubModule.js';
import EcobagSubModule from './EcobagSubModule.js';
import { computePaybackYears, formatPaybackYears } from '../../calculations/profitability.js';
import '../../../../../styles/ClientDashboard.css';
import '../../actionPlanV2.css';

const TABS = [
  { key: 'synthesis', label: 'Synthèse' },
  { key: 'aerator', label: 'Mousseurs' },
  { key: 'shower', label: 'Douches' },
  { key: 'ecobag', label: 'Écobags' },
];

const EMPTY_RESULT = { ready: false };

const HydroEfficientEquipmentModule = () => {
  const navigate = useNavigate();
  const { client_id, building_id } = useParams();

  const [equipments, setEquipments] = useState([]);
  const [usages, setUsages] = useState([]);
  const [waterPricing, setWaterPricing] = useState({ waterPrice: null, electricityPrice: null, fuelPrice: null, waterHeatingType: null, hotWaterSetpoint: null, waterHeatingEfficiency: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeTab, setActiveTab] = useState('synthesis');
  const [aeratorResult, setAeratorResult] = useState(EMPTY_RESULT);
  const [showerResult, setShowerResult] = useState(EMPTY_RESULT);
  const [ecobagResult, setEcobagResult] = useState(EMPTY_RESULT);

  /* ── Fetch equipments, then usages (once, shared by all 3 sub-modules) ── */
  useEffect(() => {
    if (!building_id) return;
    fetch(`${process.env.REACT_APP_API_URL}/load_equipments_with_rating/${building_id}`)
      .then(r => { if (!r.ok) throw new Error("Erreur lors de la récupération des équipements."); return r.json(); })
      .then(data => {
        setEquipments(data);
        return fetch(`${process.env.REACT_APP_API_URL}/load_usages_by_equipments/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });
      })
      .then(r => { if (!r.ok) throw new Error("Erreur lors de la récupération des usages."); return r.json(); })
      .then(data => { setUsages(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [building_id]);

  /* ── Fetch building (water + energy pricing, for cold and hot water cost) ── */
  useEffect(() => {
    if (!building_id) return;
    fetch(`${process.env.REACT_APP_API_URL}/get_building/${building_id}`)
      .then(r => r.json())
      .then(data => setWaterPricing({
        waterPrice: data?.global_water_data?.water_price ?? null,
        electricityPrice: data?.global_water_data?.electricity_price ?? null,
        fuelPrice: data?.global_water_data?.fuel_price ?? null,
        waterHeatingType: data?.global_water_data?.water_heating_type ?? null,
        hotWaterSetpoint: data?.global_water_data?.hot_water_setpoint ?? null,
        waterHeatingEfficiency: data?.global_water_data?.water_heating_efficiency ?? null,
      }))
      .catch(() => setWaterPricing({ waterPrice: null, electricityPrice: null, fuelPrice: null, waterHeatingType: null, hotWaterSetpoint: null, waterHeatingEfficiency: null }));
  }, [building_id]);

  // Stable callbacks so each sub-module's internal useEffect(onResultChange) doesn't re-fire needlessly
  const handleAeratorResult = useCallback((result) => setAeratorResult(result), []);
  const handleShowerResult = useCallback((result) => setShowerResult(result), []);
  const handleEcobagResult = useCallback((result) => setEcobagResult(result), []);

  const subModuleResults = [
    { key: 'aerator', label: 'Mousseurs', result: aeratorResult },
    { key: 'shower', label: 'Douches', result: showerResult },
    { key: 'ecobag', label: 'Écobags', result: ecobagResult },
  ];

  const totalSavingsM3 = subModuleResults.reduce((sum, m) => sum + (m.result.ready ? m.result.totalSavingsM3 : 0), 0);
  const totalSavingsEur = subModuleResults.reduce((sum, m) => sum + (m.result.ready && m.result.waterPriceAvailable ? m.result.savingsEurTotalPerYear : 0), 0);
  const anyWaterPriceAvailable = subModuleResults.some(m => m.result.ready && m.result.waterPriceAvailable);
  const anyConfigured = subModuleResults.some(m => m.result.ready);

  const totalEquipmentsCount = subModuleResults.reduce((sum, m) => sum + (m.result.ready ? m.result.perEquipment.length : 0), 0);
  const totalCurrentM3 = subModuleResults.reduce((sum, m) => sum + (m.result.ready ? m.result.totalCurrentM3 : 0), 0);
  const totalSavingsPct = totalCurrentM3 > 0 ? (totalSavingsM3 / totalCurrentM3) * 100 : 0;

  // A global investment total is only trustworthy if every configured module fully knows its
  // costs — a module with a manual-flow lot (no catalog price) makes the whole total a lower bound.
  const configuredModules = subModuleResults.filter(m => m.result.ready);
  const investmentFullyKnown = configuredModules.length > 0 && configuredModules.every(m => m.result.investmentFullyKnown);
  const totalInvestmentEur = investmentFullyKnown
    ? configuredModules.reduce((sum, m) => sum + m.result.totalInvestmentEur, 0)
    : null;
  const totalPaybackYears = computePaybackYears({
    investmentEur: totalInvestmentEur,
    investmentFullyKnown,
    savingsEurPerYear: totalSavingsEur,
  });

  // Hot water's share of the total € savings is only meaningful if every priced module
  // could actually compute a heating cost — otherwise it would silently understate it.
  const waterPricedModules = subModuleResults.filter(m => m.result.ready && m.result.waterPriceAvailable);
  const allHeatingAvailable = waterPricedModules.length > 0 && waterPricedModules.every(m => m.result.heatingPriceAvailable);
  const totalSavingsEurHeating = allHeatingAvailable
    ? waterPricedModules.reduce((sum, m) => sum + m.result.savingsEurHeatingPerYear, 0)
    : null;
  const hotWaterSharePct = allHeatingAvailable && totalSavingsEur > 0 ? (totalSavingsEurHeating / totalSavingsEur) * 100 : 0;

  if (loading) return (
    <div className="centered-spinner-dashboard">
      <WalterLoader />
    </div>
  );
  if (error) return <p>Erreur : {error}</p>;

  return (
    <div className="apv2-page">
      <div className="apv2-module-header">
        <button type="button" className="back-button" onClick={() => navigate(`/client/${client_id}/${building_id}/action_plan_v2`)}>
          ← Modules de chiffrage
        </button>
      </div>

      <div className="apv2-wizard-shell">
        <h1 className="apv2-wizard-title">Équipements tertiaires hydro-économes</h1>
        <div className="apv2-hub-tabs">
          {TABS.map(tab => {
            const isActive = activeTab === tab.key;
            const subResult = subModuleResults.find(m => m.key === tab.key)?.result;
            return (
              <button
                key={tab.key}
                type="button"
                className={`apv2-hub-tab ${isActive ? 'apv2-hub-tab--active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {subResult?.ready && <span className="apv2-hub-tab-dot" />}
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* All 3 sub-modules stay mounted at all times — only the CSS visibility changes on tab switch —
          so switching tabs never resets a sub-wizard's in-progress selection/step. */}
      <div className={activeTab === 'synthesis' ? '' : 'apv2-tab-hidden'}>
        <div className="apv2-step-card">
          <div className="apv2-step-header">
            <div className="apv2-step-header-text">
              <span className="apv2-step-eyebrow">Synthèse</span>
              <h2 className="apv2-step-heading">Résumé des économies</h2>
            </div>
          </div>
          <div className="apv2-step-body">
            {!anyConfigured && (
              <p className="apv2-sim-hint">Aucun sous-module n'est configuré pour le moment — ouvrez un onglet pour lancer une simulation.</p>
            )}
            <div className="apv2-results-table-wrapper apv2-synthesis-table-wrapper">
              <table className="apv2-results-table apv2-synthesis-table">
                <thead>
                  <tr>
                    <th>Module</th>
                    <th className="apv2-th-right">Équipements</th>
                    <th className="apv2-th-right">Économie d'eau</th>
                    <th className="apv2-th-right">Économie financière</th>
                    <th className="apv2-th-right">Réduction</th>
                    <th className="apv2-th-right">Investissement</th>
                    <th className="apv2-th-right">Retour sur investissement</th>
                  </tr>
                </thead>
                <tbody>
                  {subModuleResults.map(m => (
                    <tr key={m.key} className={!m.result.ready ? 'apv2-synthesis-tr--empty' : ''}>
                      <td>
                        <span className={`apv2-synthesis-status-dot ${m.result.ready ? 'apv2-synthesis-status-dot--active' : ''}`} />
                        {m.label}
                      </td>
                      {m.result.ready ? (
                        <>
                          <td className="apv2-th-right">{m.result.perEquipment.length}</td>
                          <td className="apv2-th-right">{m.result.totalSavingsM3.toFixed(1)} m³/an</td>
                          <td className="apv2-th-right">
                            {m.result.waterPriceAvailable ? (
                              <>
                                <div className="apv2-synthesis-eur-value">{m.result.savingsEurTotalPerYear.toFixed(0)} €/an</div>
                                <div className="apv2-synthesis-hotwater-line">
                                  {m.result.heatingPriceAvailable
                                    ? `dont ${m.result.savingsEurHeatingPerYear.toFixed(0)} € eau chaude (${m.result.savingsEurTotalPerYear > 0 ? (m.result.savingsEurHeatingPerYear / m.result.savingsEurTotalPerYear * 100).toFixed(0) : 0} %)`
                                    : "eau chaude non calculable"}
                                </div>
                              </>
                            ) : 'N/A'}
                          </td>
                          <td className="apv2-th-right">{m.result.savingsPct.toFixed(0)} %</td>
                          <td className="apv2-th-right">{m.result.investmentFullyKnown ? `${m.result.totalInvestmentEur.toFixed(0)} €` : 'N/A'}</td>
                          <td className="apv2-th-right">{m.result.paybackYears != null ? formatPaybackYears(m.result.paybackYears) : 'N/A'}</td>
                        </>
                      ) : (
                        <td className="apv2-synthesis-empty-cell" colSpan={6}>Non configuré</td>
                      )}
                    </tr>
                  ))}
                  <tr className="apv2-synthesis-tr--total">
                    <td>Total</td>
                    <td className="apv2-th-right">{totalEquipmentsCount}</td>
                    <td className="apv2-th-right">{totalSavingsM3.toFixed(1)} m³/an</td>
                    <td className="apv2-th-right">
                      {anyWaterPriceAvailable ? (
                        <>
                          <div className="apv2-synthesis-eur-value">{totalSavingsEur.toFixed(0)} €/an</div>
                          <div className="apv2-synthesis-hotwater-line">
                            {allHeatingAvailable
                              ? `dont ${totalSavingsEurHeating.toFixed(0)} € eau chaude (${hotWaterSharePct.toFixed(0)} %)`
                              : "part eau chaude incomplète — prix de l'énergie manquant sur un module"}
                          </div>
                        </>
                      ) : 'N/A'}
                    </td>
                    <td className="apv2-th-right">{totalCurrentM3 > 0 ? `${totalSavingsPct.toFixed(0)} %` : '—'}</td>
                    <td className="apv2-th-right">
                      {investmentFullyKnown ? `${totalInvestmentEur.toFixed(0)} €` : (configuredModules.length > 0 ? 'partiel — débit manuel sans prix' : '—')}
                    </td>
                    <td className="apv2-th-right">{totalPaybackYears != null ? formatPaybackYears(totalPaybackYears) : '—'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div className={activeTab === 'aerator' ? '' : 'apv2-tab-hidden'}>
        <AeratorSubModule equipments={equipments} usages={usages} waterPricing={waterPricing} onResultChange={handleAeratorResult} />
      </div>

      <div className={activeTab === 'shower' ? '' : 'apv2-tab-hidden'}>
        <ShowerSubModule equipments={equipments} usages={usages} waterPricing={waterPricing} onResultChange={handleShowerResult} />
      </div>

      <div className={activeTab === 'ecobag' ? '' : 'apv2-tab-hidden'}>
        <EcobagSubModule equipments={equipments} usages={usages} waterPricing={waterPricing} onResultChange={handleEcobagResult} />
      </div>
    </div>
  );
};

export default HydroEfficientEquipmentModule;
